import type { userAgent } from 'next/server';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import useApexDomain from '@alltrails/context/hooks/useApexDomain';
import useLoadAppsFlyer from '@alltrails/core/hooks/useLoadAppsFlyer';
import { useExperiments } from '@alltrails/experiments';
import { useSessionStorage } from '@alltrails/session-storage';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import { InstallPromptType } from '../types';
import { getAFOneLinkConfig } from '../utils/getAFOneLinkConfig';
import getAnalyticsParams from '../utils/getAnalyticsParams';
import type InstallPromptTrigger from '@alltrails/amplitude/enums/InstallPromptTrigger';

type AppsFlyerDeepLinkParams = {
  deeplinkData?: DeeplinkData;
  customAppPath?: string;
  displayTrigger?: CarouselDisplayTrigger | InstallPromptTrigger;
  isEnabled: boolean;
  isOpen?: boolean;
  promptType?: InstallPromptType;
};

const oneLinkFallback = 'https://alltrails.onelink.me/EFvD/hvsmar15';

const useAppsFlyerDeeplink = ({ isEnabled, isOpen, deeplinkData, customAppPath, promptType, displayTrigger }: AppsFlyerDeepLinkParams) => {
  const apexDomain = useApexDomain();
  const browserName = useSelector((state: { browser?: ReturnType<typeof userAgent>['browser'] }) => state.browser?.name);
  const isAFLoaded = useLoadAppsFlyer(isEnabled);
  const experiments = useExperiments();
  const { getValue: getSessionAttribution } = useSessionStorage('sessionAttribution');

  const getDeeplink = useCallback(() => {
    if (!isEnabled || !isAFLoaded) {
      return oneLinkFallback;
    }

    let appPath = 'screen/explore';

    if (customAppPath) {
      appPath = customAppPath;
    } else if (deeplinkData) {
      const { pageType, pageId, startDownload } = deeplinkData;

      if (pageType === 'navigate') {
        appPath = `screen/recorder?trailID=${pageId}&start=false`;
      } else {
        appPath = `${pageType}/${pageId}`;
        if (startDownload && pageType === 'trail') {
          appPath += '?startDownload=true';
        }
      }
    }

    const analyticsParams = getAnalyticsParams({ promptType, browserName, displayTrigger, experiments });
    const linkConfig = getAFOneLinkConfig({
      apexDomain,
      deepLinkPath: appPath,
      analyticsParams,
      sessionAttribution: getSessionAttribution()
    });

    const resultUrl = window.AF_SMART_SCRIPT.generateOneLinkURL(linkConfig);

    // Impressions should be triggered after calling generateOneLinkURL, and after a timeout per the AppsFlyer documentation:
    // https://dev.appsflyer.com/hc/docs/dl_smart_script_v2#fire-an-impression
    if (isOpen) {
      setTimeout(() => {
        window.AF_SMART_SCRIPT.fireImpressionsLink();
      }, 1000);
    }

    return resultUrl?.clickURL || oneLinkFallback;
  }, [
    isEnabled,
    isAFLoaded,
    deeplinkData,
    customAppPath,
    promptType,
    browserName,
    displayTrigger,
    experiments,
    apexDomain,
    getSessionAttribution,
    isOpen
  ]);

  return getDeeplink;
};

export default useAppsFlyerDeeplink;
